import { useEffect } from 'react';

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';

import { AxiosCartError, CartErrorTypes, ProductVariant } from '@telialt/models/src/EshopModel';
import { clearCartSession, toCartError } from '@telialt/utils/src/eshopUtils';
import { useSegment } from '@telialt/utils/src/segment';

import { ESHOP_QUERY_KEY } from '../../model';
import { apiGetProduct } from '../../store/api';

const filteringParams = {
    fields:
        'images(FULL),standardPrice(FULL),summary,classifications,description,shortDescription,shortDescription2,shortDescription3,scalaId,code,stock(FULL),categoryUrl,' +
        'url,energyClass,energyConsumingSupported,energyLabel(FULL),productInformationSheet(FULL),insurancePossible,productReferences',
};

/**
 * fetch Product Variant data
 * @param {string} productVariantCode - The code of the product variant (gift is also a product variant)
 * @param {boolean} [isGift=false] - Flag indicating if the product is a gift, required to retrieve hidden gift data
 */
export const useProductVariant = (productVariantCode: string, isGift: boolean = false) => {
    const queryClient = useQueryClient();
    const segment = useSegment();

    const {
        data: productVariant,
        isLoading: productVariantIsLoading,
        error: productVariantError,
    } = useQuery<AxiosResponse<ProductVariant>, AxiosCartError, ProductVariant>({
        queryKey: [ESHOP_QUERY_KEY.PRODUCT_VARIANT, productVariantCode, isGift, segment],
        queryFn: async ({ signal }) =>
            apiGetProduct<ProductVariant>(segment, productVariantCode, { ...filteringParams, gift: isGift }, signal),
        select: ({ data }) => data,
        enabled: !!productVariantCode,
        staleTime: 3000,
        retry: false,
        retryOnMount: false,
    });

    useEffect(() => {
        if (productVariantError) {
            const cartError = toCartError(productVariantError);
            if (cartError.status === 400 && cartError.type === CartErrorTypes.UnknownIdentifierError) {
                clearCartSession();
                if (!queryClient.isFetching({ queryKey: [ESHOP_QUERY_KEY.BASE_PRODUCT] })) {
                    queryClient.invalidateQueries({ queryKey: [ESHOP_QUERY_KEY.BASE_PRODUCT] });
                }
                if (!queryClient.isFetching({ queryKey: [ESHOP_QUERY_KEY.CART] })) {
                    queryClient.invalidateQueries({ queryKey: [ESHOP_QUERY_KEY.CART] });
                }
            }
        }
    }, [productVariantError]);

    return {
        productVariant,
        productVariantIsLoading,
        productVariantError,
    };
};
